import { Injectable } from '@angular/core';
import { Chart, ChartType } from 'chart.js';
import { mergeDeep } from '@client/app/shared/utils/object-utils';

export interface ChartJsOptions {
    responsive?: boolean;
    [key: string]: any;
}

@Injectable({
    providedIn: 'root',
})
export class ChartJsProvider {
    private options: ChartJsOptions = { responsive: true, maintainAspectRatio: false };

    constructor () {
        // Set default Chart.js configuration
        // In v3, defaults are at Chart.defaults directly, not Chart.defaults.global

        // Tooltip defaults - in v3, tooltips are under plugins.tooltip
        if (Chart.defaults.plugins?.tooltip) {
            Chart.defaults.plugins.tooltip.mode = 'index';
        }

        // Element defaults
        if (Chart.defaults.elements?.line) {
            Chart.defaults.elements.line.borderWidth = 2;
        }
        if (Chart.defaults.elements?.bar) {
            Chart.defaults.elements.bar.borderWidth = 2;
        }

        // Legend defaults - in v3, legend is under plugins.legend
        if (Chart.defaults.plugins?.legend) {
            Chart.defaults.plugins.legend.display = false;
        }

        // Store colors in our options (v3 doesn't have global colors)
        this.options.chartColors = [
            '#97BBCD', // blue
            '#DCDCDC', // light grey
            '#F7464A', // red
            '#46BFBD', // green
            '#FDB45C', // yellow
            '#949FB1', // grey
            '#4D5360', // dark grey
        ];


        // Check for excanvas support (legacy IE)
        const useExcanvas = typeof (window as any).G_vmlCanvasManager === 'object' &&
      (window as any).G_vmlCanvasManager !== null &&
      typeof (window as any).G_vmlCanvasManager.initElement === 'function';

        if (useExcanvas) {
            Chart.defaults.animation = false;
        }
    }

    /**
   * Allow to set global options during configuration
   */
    setOptions (type: ChartType | ChartJsOptions, customOptions?: ChartJsOptions): void {
    // If no type was specified set option for the global object
        if (!customOptions) {
            const globalOptions = type as ChartJsOptions;
            this.options = mergeDeep(this.options, globalOptions);
        } else {
            // Set options for the specific chart
            const chartType = type as ChartType;
            this.options[chartType] = mergeDeep(this.options[chartType] || {}, customOptions);
        }

        // Merge with Chart.js defaults
        Object.assign(Chart.defaults, this.options);
    }

    /**
   * Get options for a specific chart type
   */
    getOptions (type?: ChartType): ChartJsOptions {
        const typeOptions = type && this.options[type] || {};
        return mergeDeep({}, this.options, typeOptions);
    }

    /**
   * Get Chart.js constructor
   */
    getChart (): typeof Chart {
        return Chart;
    }
}
